import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import { companyResetPasswordApiClient } from '@src/api/company/auth/companyResetPasswordApiClient';
import { PasswordRuleDescription } from '@src/components/PasswordRuleDescription';
import { TogglePassword } from '@src/features/company/auth/components/TogglePassword';
import { useToast } from '@src/features/toast/context/toastContext';
import TOAST_MESSAGE from '@src/modules/constants/toastMessage';
import { COMPANY_URL } from '@src/routes/company';
import Button from '@src/ui/elements/button/Button';
import { ErrorMessage } from '@src/ui/elements/form';
import { firstError } from '@src/utils/errorMessageUtils';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

function ResetPasswordForm() {
  type requestParam = {
    token: string | undefined;
    email: string;
    new_password: string;
    new_password_confirmation: string;
  };

  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const [email, setEmail] = useState('');
  const toast = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<requestParam>();

  useEffect(() => {
    const fetchUserInfo = async () => {
      if (token) {
        const response = await companyResetPasswordApiClient.token(token);
        handleApiResponse(response, (data) => {
          setEmail(data.email);
        });
      }
    };

    fetchUserInfo();
  }, [token]);

  const onSubmit: SubmitHandler<requestParam> = async (data) => {
    clearErrors();
    const response = await companyResetPasswordApiClient.update({
      token: token,
      email: email,
      new_password: data.new_password,
      new_password_confirmation: data.new_password_confirmation,
    });
    handleApiResponse(
      response,
      () => {
        toast(TOAST_MESSAGE.UPDATE);
        navigate(COMPANY_URL.LOGIN);
      },
      (errors) => {
        setError('new_password', {
          type: 'manual',
          message: firstError(errors.new_password),
        });
        setError('new_password_confirmation', {
          type: 'manual',
          message: firstError(errors.new_password_confirmation),
        });
      },
    );
  };

  const [isRevealNewPassword, setIsRevealNewPassword] = useState(false);
  const [isRevealNewPasswordConfirmation, setIsRevealNewPasswordConfirmation] = useState(false);

  const toggle = (setter: React.Dispatch<React.SetStateAction<boolean>>) => {
    setter((prevState) => !prevState);
  };

  return (
    <div className='flex flex-col gap-8'>
      <p className='text-center font-semibold'>新しいパスワードを設定してください</p>
      <p className=''>{email}</p>
      <form
        className='flex flex-col gap-6'
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className='flex flex-col gap-[26px]'>
          <div className='flex flex-col gap-4'>
            <div className='flex flex-col gap-[6px]'>
              <label
                className='label-login'
                htmlFor='password'
              >
                新しいパスワード
              </label>
              <div className='relative'>
                <input
                  id='password'
                  type={isRevealNewPassword ? 'text' : 'password'}
                  className={`input-login ${errors.new_password ? `error-input` : ``}`}
                  placeholder='パスワード'
                  {...register('new_password')}
                />
                <button
                  type='button'
                  onClick={() => toggle(setIsRevealNewPassword)}
                  className='toggle-password'
                >
                  <TogglePassword isReveal={isRevealNewPassword} />
                </button>
              </div>
              <PasswordRuleDescription />
              {errors.new_password && <ErrorMessage>{errors.new_password.message!}</ErrorMessage>}
            </div>
            <div className='flex flex-col gap-[6px]'>
              <label
                className='label-login'
                htmlFor='password'
              >
                新しいパスワード(確認用)
              </label>
              <div className='relative'>
                <input
                  id='password'
                  type={isRevealNewPasswordConfirmation ? 'text' : 'password'}
                  className={`input-login ${errors.new_password_confirmation ? `error-input` : ``}`}
                  placeholder='パスワード(確認用)'
                  {...register('new_password_confirmation')}
                />
                <button
                  type='button'
                  onClick={() => toggle(setIsRevealNewPasswordConfirmation)}
                  className='toggle-password'
                >
                  <TogglePassword isReveal={isRevealNewPasswordConfirmation} />
                </button>
              </div>
              {errors.new_password_confirmation && (
                <ErrorMessage>{errors.new_password_confirmation.message!}</ErrorMessage>
              )}
            </div>
          </div>
          <Button
            type='submit'
            fullRounded
            fullWidth
          >
            パスワードを更新
          </Button>
        </div>
      </form>
    </div>
  );
}

export default ResetPasswordForm;
